/**
 * Landing.tsx — GenWizard landing page.
 *
 * Tiles are now gated by the user's *entitlements* (from /me), not a hardcoded
 * is_admin check. Entitlements come from access_config.json on the server:
 *   modules: which of DQ / MDM / DG are visible.
 * Falls back to the old rule (admin → all, else DG) if entitlements are absent,
 * so nothing breaks if the backend hasn't been updated yet.
 */

import React from 'react';
import { injectTheme, injectLandingTheme, T } from './theme/theme';
import AppHeader from './common/AppHeader';
import { useAuth } from './hooks/useAuth';

// ─── Sub-components ───────────────────────────────────────────────────────────

interface TileProps {
  idx: string;
  icon: string;
  iconBg: string;
  title: string;
  desc: string;
  color: 'cyan' | 'green' | 'amber' | 'purple';
  actions: { label: string; cls: string; onClick: () => void }[];
}

function Tile({ idx, icon, iconBg, title, desc, color, actions }: TileProps) {
  return (
    <div className={`gwl-tile gwl-rise ${color}`}>
      <div className="gwl-tile-header">
        <div className="gwl-tile-icon-wrap" style={{ background: iconBg }}>
          {icon}
        </div>
        <div className="gwl-tile-idx">{idx}</div>
      </div>
      <div className="gwl-tile-title">{title}</div>
      <div className="gwl-tile-desc">{desc}</div>
      <div className="gwl-cta-stack">
        {actions.map((a, i) => (
          <button key={i} className={`gwl-cta ${a.cls}`} onClick={a.onClick}>
            {a.label}
            <span className="gwl-cta-arr">→</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  {
    color: T.brand,
    bg: T.brandLight,
    title: 'AI-Powered Rule Generation',
    desc: 'Profile data and auto-generate DQ rules with confidence scoring',
  },
  {
    color: T.purple,
    bg: T.purpleBg,
    title: 'Governance & Compliance',
    desc: 'Audit-ready policy artifacts aligned to enterprise standards',
  },
  {
    color: T.amber,
    bg: T.amberBg,
    title: 'MDM Modernization Accelerators',
    desc: 'Migrate legacy configurations with AI-guided transformation',
  },
];

// ─── Main ─────────────────────────────────────────────────────────────────────

interface Props {
  setTool: (t: string) => void;
}

export default function GenWizardLanding({ setTool }: Props) {

  const { user } = useAuth();

  // Entitlements come from /me ({ entitlements: { modules: [...] } }). If the
  // backend hasn't been updated yet, fall back to the previous hardcoded rule.
  const ent = (user as any)?.entitlements;
  const allowedModules: string[] =
    Array.isArray(ent?.modules) && ent.modules.length
      ? ent.modules
      : (user?.is_admin ? ['DQ', 'MDM', 'DG'] : ['DG']);

  const can = (m: 'DQ' | 'MDM' | 'DG') => allowedModules.includes(m);
  const tileCount = (['DQ', 'MDM', 'DG'] as const).filter(can).length || 1;

  React.useEffect(() => {
    injectTheme();
    injectLandingTheme();
  }, []);

  return (
    <div className="gwl-root">
      {/* ── Shared header — no app/tool context on landing ── */}
      <AppHeader />

      {/* ── Hero ── */}
      <section className="gwl-hero">
        <div>
          <div className="gwl-hero-eyebrow gwl-hero-rise">Intelligent Data Agents</div>
          <h1 className="gwl-hero-title gwl-hero-rise">
            Every step of the<br />
            <em>data-to-insight</em><br />
            journey for DMG
          </h1>
          <p className="gwl-hero-desc gwl-hero-rise">
            AI-powered governance, data quality, and master data management —
            from raw ingestion to audit-ready enterprise insight.
          </p>
        </div>

        <div className="gwl-hero-right gwl-hero-rise">
          {FEATURES.map((f) => (
            <div key={f.title} className="gwl-feature-item">
              <div
                className="gwl-feature-dot"
                style={{ background: f.color, boxShadow: `0 0 0 3px ${f.bg}` }}
              />
              <div>
                <div className="gwl-feature-title">{f.title}</div>
                <div className="gwl-feature-desc">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section divider ── */}
      <div className="gwl-divider">
        <div className="gwl-divider-line" />
        <div className="gwl-divider-label">Mission Modules</div>
        <div className="gwl-divider-line" />
      </div>

      {/* ── Tiles ── */}
      <main
        className="gwl-tiles"
        style={{
          gridTemplateColumns: `repeat(${tileCount}, minmax(0, 1fr))`,
          justifyItems: 'center',
          maxWidth: tileCount === 1 ? 420 : undefined,
          margin: tileCount === 1 ? '0 auto' : undefined,
        }}
      >
        {can('DQ') && (
          <Tile
            idx="01 / DQ"
            icon="📏"
            iconBg={T.brandLight}
            title="Define DQ Rules & Governance"
            desc="Identify data quality issues across master and transaction data, finalize validation rules, and shape governance policies aligned with business standards."
            color="cyan"
            actions={[
              {
                label: 'Finalize Rules & Policies',
                cls: 'cyan-c',
                onClick: () => setTool('BUILD_DQ_USING_DATA'),
              },
            ]}
          />
        )}

        {can('MDM') && (
          <Tile
            idx="02 / MDM"
            icon="🧠"
            iconBg={T.greenBg}
            title="Master Data Health & Modernization"
            desc="Evaluate the health of your existing master data, detect quality and duplication issues, and receive AI-powered recommendations to modernize your MDM model."
            color="green"
            actions={[
              {
                label: 'Launch E2E Data Management Solution',
                cls: 'green-c',
                onClick: () => setTool('BUILD_MDM_USING_DATA'),
              },
            ]}
          />
        )}

        {can('DG') && (
          <Tile
            idx="03 / GOV"
            icon="🚀"
            iconBg={T.amberBg}
            title="Data Governance"
            desc="Autonomous metadata discovery, explainable compliance, policy-based anonymization, and audit-ready governance artifacts for scalable, trusted enterprise data management."
            color="amber"
            actions={[
              {
                label: 'Launch Governance Suite',
                cls: 'amber-c',
                onClick: () => setTool('BUILD_DG_USING_DATA'),
              },
            ]}
          />
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="gwl-footer">
        <div className="gwl-footer-txt">© 2025 Accenture · All rights reserved</div>
        <div className="gwl-footer-txt">Build 2025.06</div>
      </footer>
    </div>
  );
}